/*
  GTH cart — AJAX add for forms the theme does not manage.
  Rewritten 5 Aug 2026. Revised 13 Aug 2026 for speed.

  WHY THIS EXISTS
  Several sections (artisan rails, vault cards, seasonal B, collection trial and
  the Boost grids) render their own hand-rolled <form action="/cart/add">. The
  theme's add-to-cart controller only binds to buttons inside
  [data-add-action-wrapper], so those forms fall through to a native browser POST
  and the customer is thrown onto /cart. This intercepts them instead.

  WHY IT IS SAFE TO BE GREEDY
  When the theme DOES manage a form it binds on the button's click and calls
  preventDefault(), so no submit event is ever emitted. Anything that actually
  reaches this listener is by definition unmanaged. The single exception is the
  theme's own escape hatch, [data-ajax-disable="true"], which we honour.

  AFTER A SUCCESSFUL ADD we speak the theme's own language rather than
  reimplementing its UI (event names verified against assets/theme.js):
    theme:cart:change on document, bubbles, detail.cart  -> header counts,
                                                            free-shipping bar,
                                                            drawer marked stale
    theme:cart:reload on document, bubbles               -> drawer refetches
                                                            ?section_id=api-cart-items
    theme:cart:popdown on document, detail.variant.id    -> renders the
                                                            confirmation bar
  Order matters: change before reload, or the drawer can render itself empty.

  Confirmation is the theme's product popdown, not the cart drawer — see
  gth-cart-fast.js for why. The drawer is still the fallback if the popdown
  wrapper is missing from a template, and a full navigation to /cart is the
  fallback after that (i.e. if the Cart type theme setting is ever put back to
  Page). On any error it falls back to a native form submit so an add is never
  silently lost.

  AUG 13 2026 — PAIRED ADDS IN ONE WRITE
  A cart write costs ~950ms here. A product carrying a free-item pairing (Weng
  scoops, Dharma Gate / Cloud 357g) used to cost two of them: the customer's add,
  then a second add from the free-item pass once it had re-read the cart. When
  the pairing is already known on the client we now send both lines in the SAME
  /cart/add.js call, which halves the wait and removes the flicker of the gift
  appearing a second later.

  That is also why this file now carries a capture-phase click handler. Paired
  products are frequently sold through theme-managed forms (the PDP), which never
  emit a submit event, so the submit listener alone could not reach them. The
  click handler intercepts ONLY when a pairing exists for the selected variant
  and the form can be read unambiguously; in every other case it returns
  immediately and the theme handles the add exactly as before.

  Loaded once from snippets/scoop-bag-auto-add.liquid, after gth-cart-fast.js.
  gth-seasonal.liquid and gth-brewing.liquid also load it, hence the init guard.
*/
(function () {
  if (window.__gthCartInit) return;
  window.__gthCartInit = true;

  var HDR = { 'X-Gth-Cart': '1' };

  function isOurs(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return false;
    if (form.querySelector('[type="file"]')) return false;
    if (document.querySelector('[data-ajax-disable="true"]')) return false;
    return true;
  }

  function pairFor(variantId) {
    var api = window.GthFreeItems;
    if (!api || !variantId || typeof api.pairFor !== 'function') return null;
    return api.pairFor(String(variantId));
  }

  function itemFromForm(form) {
    var data;
    try { data = new FormData(form); } catch (e) { return null; }

    var id = data.get('id');
    if (!id || !/^\d+$/.test(String(id))) return null;

    var quantity = parseInt(data.get('quantity'), 10);
    if (!quantity || quantity < 1) quantity = 1;

    var item = { id: Number(id), quantity: quantity };

    var plan = data.get('selling_plan');
    if (plan && /^\d+$/.test(String(plan))) item.selling_plan = Number(plan);

    var properties = {};
    var found = false;
    data.forEach(function (value, key) {
      var match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).length) {
        properties[match[1]] = value;
        found = true;
      }
    });
    if (found) item.properties = properties;

    return item;
  }

  function withPairing(item) {
    var pair = pairFor(item.id);
    if (!pair || !pair.gift) return [item];

    var propKey = (window.GthFreeItems && window.GthFreeItems.PROP_KEY) || '_scoop_variant';
    var giftProperties = {};
    giftProperties['Included with'] = pair.title;
    giftProperties[propKey] = String(item.id);

    return [item, { id: Number(pair.gift), quantity: item.quantity, properties: giftProperties }];
  }

  function announce() {
    return fetch('/cart.js', { headers: HDR })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (window.GthCart) window.GthCart.set(cart);
        document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        return cart;
      });
  }

  function confirmAdd(variantId) {
    if (window.GthCart && window.GthCart.popdown(variantId)) return true;
    var node = document.querySelector('[data-drawer="drawer-cart"]');
    if (!node) return false;
    node.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
    return true;
  }

  function post(body, asJson) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: asJson
        ? { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Gth-Cart': '1' }
        : { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest', 'X-Gth-Cart': '1' },
      body: asJson ? JSON.stringify(body) : body
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    });
  }

  function run(form, button, body, asJson, variantId) {
    var original = button ? button.innerHTML : null;
    var adding = button && button.getAttribute('data-adding-label');

    if (button) {
      button.setAttribute('disabled', 'disabled');
      if (adding) button.innerHTML = adding;
    }

    return post(body, asJson)
      .then(announce)
      .then(function () {
        if (button) {
          button.removeAttribute('disabled');
          var added = button.getAttribute('data-added-label');
          if (added) {
            button.innerHTML = added;
            setTimeout(function () { button.innerHTML = original; }, 2200);
          } else if (original !== null) {
            button.innerHTML = original;
          }
        }
        if (!confirmAdd(variantId)) window.location = '/cart';
      })
      .catch(function () {
        if (window.GthCart) window.GthCart.hide();
        if (button) {
          button.removeAttribute('disabled');
          if (original !== null) button.innerHTML = original;
        }
        form.submit();
      });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-add-to-cart], button[name="add"], input[name="add"]');
    if (!button || button.disabled || button.hasAttribute('disabled')) return;

    var form = button.closest('form');
    if (!isOurs(form)) return;

    var item = itemFromForm(form);
    if (!item) return;

    var items = withPairing(item);
    if (items.length < 2) return;

    event.preventDefault();
    event.stopPropagation();
    run(form, button, { items: items }, true, item.id);
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!isOurs(form)) return;
    event.preventDefault();

    var button = form.querySelector('[type="submit"]') || form.querySelector('button');
    var item = itemFromForm(form);

    if (item) {
      run(form, button, { items: withPairing(item) }, true, item.id);
    } else {
      var field = form.querySelector('[name="id"]');
      run(form, button, new URLSearchParams(new FormData(form)).toString(), false, field && field.value);
    }
  }, true);
})();
